import {
	DollarOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	FileDoneOutlined,
	ReloadOutlined,
	CloseOutlined,
	DownloadOutlined,
	FileExcelOutlined,
	FileTextOutlined,
} from '@ant-design/icons';
import { payrollApi } from 'Api/payroll';
import {
	Button,
	Modal,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip,
	Typography,
	Divider,
} from 'antd';
import Column from 'antd/es/table/Column';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import Ellipse from 'components/Global/Ellipse';
import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
	ApprovedByAccounts,
	CrossRejectedSvg,
	DeleteActionSvg,
	EditActionSvg,
	LoanProcessing,
	PendingForApproval,
} from 'utils/svgs';
import { formatNumber, toastText } from 'utils/utils';
import AddModal from '../AddModal';
import styles from './index.module.scss';
import './index.scss';
import DownloadComponent from 'components/Global/Download';

const { Text } = Typography;

type TableProps = {
	tableData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	isLoading: boolean;
	showDeleteModal: any;
	setDeletePayroll?: any;
	fetchAllPayrolls?: any;
};

const PayrollTable = (props: TableProps) => {
	const {
		tableData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		isLoading,
		showDeleteModal,
		setDeletePayroll,
		fetchAllPayrolls,
	} = props;

	const navigate = useNavigate();

	const context = useContext(PermissionContext);
	const isEdit = context.allowedPermissions.includes('Edit_Run_Payroll');
	const isView = context.allowedPermissions.includes('View_Run_Payroll');
	const isDelete = context.allowedPermissions.includes('Delete_Run_Payroll');
	const isAddInitiatePayment = context.allowedPermissions.includes(
		'Add_Initiate_Payment'
	);
	const [isModalVisible, setIsModalVisible] = useState(false);

	const { data } = useSelector((state: any) => state?.userProfile);

	const [bankDetailsData, setBankDetailsData] = useState([]);
	const [isBankLoading, setIsBankLoading] = useState(false);
	const [bankFileLoading, setBankFileLoading] = useState<{
		[key: string]: boolean;
	}>({});
	const [checkStatusLoading, setCheckStatusLoading] = useState<{
		[key: string]: boolean;
	}>({});
	const [selectedPayrollId, setSelectedPayrollId] = useState<string | null>(
		null
	);
	const [selectedPayrollCurrency, setSelectedPayrollCurrency] = useState<
		string | null
	>(null);

	const [isBankErrorModalVisible, setIsBankErrorModalVisible] =
		useState(false);

	// New state for bank files modal
	const [isBankFilesModalVisible, setIsBankFilesModalVisible] =
		useState(false);
	const [selectedPayrollForBankFiles, setSelectedPayrollForBankFiles] =
		useState<string | null>(null);
	const [bankFileDownloadLoading, setBankFileDownloadLoading] = useState<{
		[key: string]: boolean;
	}>({});

	const HandleViewModal = (id: string) => {
		navigate(`/run-payroll?payrollId=${id}&onlyView=true`);
	};

	// Function to open the modal
	const showModal = (payrollId: string) => {
		fetchCompanyBankDetails(payrollId);
	};

	const handleCancel = () => {
		fetchAllPayrolls();
		setIsModalVisible(false);
	};

	const fetchCompanyBankDetails = async (payrollId: string) => {
		const findPayroll = tableData.find(
			(payroll: any) => payroll.id === payrollId
		);

		try {
			setIsBankLoading(true);
			const bankDetails = await payrollApi.getBankDetailsForPayroll(
				findPayroll.currency
			);
			const details = bankDetails?.data?.data || [];

			if (details.length === 0) {
				setIsBankErrorModalVisible(true);
				return;
			}
			setBankDetailsData(bankDetails.data.data);
			setIsModalVisible(true);
		} catch (error: any) {
			let message = 'Something went wrong in fetching bank details';

			if (error.response.data.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setIsBankLoading(false);
		}
	};

	const deleteDataHandler = (id: any) => {
		setDeletePayroll(id);
		showDeleteModal();
	};

	const handleModalOpen = (payrollId: string) => {
		showModal(payrollId);
		setSelectedPayrollId(payrollId);
	};

const handleBankFileDownload = async (
	payrollId: string,
	bankType: string,
	fileType: 'csv' | 'excel' = 'csv'
) => {
	try {
		// Include fileType in the loading key
		const loadingKey = `${payrollId}_${bankType}_${fileType}`;
		setBankFileDownloadLoading((prev) => ({
			...prev,
			[loadingKey]: true,
		}));

		const response = await payrollApi.exportBankCsv(
			payrollId,
			bankType,
			fileType
		);

		const payroll = tableData.find((data: any) => data.id === payrollId);

		// Determine filename based on bank type and file type
		const bankName = bankType.replace(/_/g, '_').toLowerCase();
		const fileExtension = fileType === 'excel' ? 'xlsx' : 'csv';
		const fileName = `${payroll?.payPeriod}_${bankName}_file.${fileExtension}`;

		// Set appropriate MIME type based on file type
		const mimeType =
			fileType === 'excel'
				? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				: 'text/csv';

		const blob = new Blob([response.data], { type: mimeType });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// Clean up the URL object
		URL.revokeObjectURL(url);

		// Success message with bank type and file type
		toastText(
			`${bankType.replace(
				/_/g,
				' '
			)} ${fileType.toLowerCase()} file downloaded successfully`,
			'success'
		);
	} catch (error: any) {
		let message = `Something went wrong in fetching ${fileType} file`;

		if (error?.response?.data?.message) {
			message = error.response.data.message;
		}
		toastText(message, 'error');
	} finally {
		// Include fileType in the loading key for cleanup
		const loadingKey = `${payrollId}_${bankType}_${fileType}`;
		setBankFileDownloadLoading((prev) => ({
			...prev,
			[loadingKey]: false,
		}));
	}
};

	const handleBankDetails = async (data: any) => {
		setBankDetailsData(data);
	};

	const handleCheckStatus = async (payrollId: string) => {
		try {
			setCheckStatusLoading((prev) => ({ ...prev, [payrollId]: true }));
			const response = await payrollApi.createPayrollHistory({
				payrollId,
			});
			toastText(response.data.message, 'success');
			fetchAllPayrolls();
		} catch (error: any) {
			let message = 'Something went wrong in checking status';

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setCheckStatusLoading((prev) => ({ ...prev, [payrollId]: false }));
		}
	};

	const handleRegenerate = async (payrollId: string) => {
		try {
			const response = await payrollApi.regeneratePayslip({
				payrollId,
			});
			toastText(
				'Processing over 100 employees may take some time. Please check back later.',
				'success'
			);
			fetchAllPayrolls();
		} catch (error: any) {
			let message = 'Something went wrong in regenerating payslip';

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		}
	};

	const handleErrorModal = () => {
		setIsBankErrorModalVisible(false);
	};

	const handleRedirect = () => {
		handleErrorModal();
		navigate('/settings/configurations/company-setup?tab=bankDetails');
	};

	// New handlers for bank files modal
	const handleOpenBankFilesModal = (payrollId: string) => {
		setSelectedPayrollForBankFiles(payrollId);
		setIsBankFilesModalVisible(true);
	};

	const handleCloseBankFilesModal = () => {
		setIsBankFilesModalVisible(false);
		setSelectedPayrollForBankFiles(null);
	};

	const handleBankFileDownloadFromModal = async (
		bankType: string,
		bankName: string
	) => {
		if (selectedPayrollForBankFiles) {
			await handleBankFileDownload(selectedPayrollForBankFiles, bankType);
		}
	};

	return (
		<>
			<div style={{ width: '100%', overflowX: 'auto' }}>
				<Table
					dataSource={tableData}
					scroll={
						tableData.length > 0
							? { x: 1500, y: 'calc(100vh - 360px)' }
							: undefined
					}
					// style={{ width: '100%' }}
					pagination={{
						total: totalRecords,
						current: currentPage,
						pageSize: pageSize,
						showSizeChanger: false,
					}}
					onChange={tableChangeHandler}
					rowKey={(record) => record.id}
					loading={isLoading}
				>
					<Column
						title="Pay Period"
						dataIndex="payPeriod"
						key="payPeriod"
						width={320}
						fixed="left"
						className="bg-white"
						render={(text, record: any) => {
							const monthlyText = record.isMonthlyImport
								? 'Monthly logs : Imported sheet'
								: 'Monthly logs : System entries';

							const hourlyText = record.isImportedSheetData
								? 'Hourly logs : Imported sheet'
								: 'Hourly logs : System entries';

							const tooltipText = `${monthlyText}  ${hourlyText}`;
							const payPeriodText =
								record.status === 'APPROVED' && isView ? (
									<span
										style={{
											cursor: 'pointer',
											color: 'blue',
										}}
										onClick={() =>
											HandleViewModal(record.id)
										}
									>
										{text}
									</span>
								) : (
									<span>{text}</span>
								);
							return (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 6,
									}}
								>
									{payPeriodText}
									<Tooltip title={tooltipText}>
										<ExclamationCircleOutlined
											style={{
												color: '#584495',
												cursor: 'pointer',
												fontSize: 14,
											}}
										/>
									</Tooltip>
								</div>
							);
						}}
					/>
					<Column
						title="Pay Group"
						dataIndex="payGroups"
						key="payGroups"
						width={200}
						sorter={false}
						className="bg-white"
						render={(text, record: any) => {
							return (
								<>
									<Ellipse
										message={record.payGroups.join(' , ')}
										maxLength={18}
										key={record.id}
										tooltipMessage={record.payGroups.join(
											' , '
										)}
										isTooltip={true}
									/>
									<span></span>
								</>
							);
						}}
					/>
					<Column
						title="Currency"
						dataIndex="currency"
						key="currency"
						width={100}
						align="center"
						sorter={false}
						className="bg-white"
					/>
					<Column
						title="Combine Pay Slips"
						dataIndex="paySlips"
						key="paySlips"
						width={200}
						align="center"
						sorter={false}
						className="bg-white"
						render={(text, record: any) => (
							<div className="pay-slip-icon">
								{record?.paySlipUrl ? (
									<>
										<DownloadComponent
											documentUrl={record.paySlipUrl}
											text={record.paySlipName}
											downloadText={record.paySlipName}
											showTooltip={false}
											isLink={true}
											maxLength={20}
										/>
										{(record.status === 'PENDING' ||
											record.status === 'DRAFT') && (
											<Tooltip title="Regenerate Payslip">
												<Button
													type="text"
													icon={<ReloadOutlined />}
													onClick={() =>
														handleRegenerate(
															record.id
														)
													}
													style={{ color: '#584495' }}
												/>
											</Tooltip>
										)}
									</>
								) : (
									'-'
								)}
							</div>
						)}
					/>
					<Column
						title="Submitted By"
						dataIndex="createdBy"
						key="createdBy"
						width={130}
						align="center"
						sorter={false}
						className="bg-white"
					/>
					<Column
						title="Total Payable"
						dataIndex="totalPayable"
						key="totalPayable"
						align="center"
						width={150}
						sorter={true}
						className="bg-white"
						render={(value: string, record: any) => {
							if (record.currency === 'USD') {
								return formatNumber(
									Number(value),
									data?.CompanyCurrencies,
									true
								);
							} else {
								return formatNumber(
									Number(value),
									data?.CompanyCurrencies
								);
							}
						}}
					/>
					<Column
						title="Status"
						dataIndex="status"
						align="center"
						key="status"
						width={150}
						className="bg-white"
						render={(value) => {
							let statusText = '';
							let statusClass = '';
							let StatusIcon = null;

							switch (value) {
								case 'PENDING':
									statusText = 'Pending';
									statusClass =
										styles['status-pending-for-approval'];
									StatusIcon = <PendingForApproval />;
									break;
								case 'APPROVED':
									statusText = 'Approved';
									statusClass =
										styles['status-approved-by-accounts'];
									StatusIcon = <ApprovedByAccounts />;
									break;
								case 'REJECTED':
									statusText = 'Rejected';
									statusClass =
										styles['status-rejected-by-accounts'];
									StatusIcon = <CrossRejectedSvg />;
									break;
								case 'DRAFT':
									statusText = 'Draft';
									statusClass = styles['status-draft'];
									StatusIcon = <LoanProcessing />;
									break;
								default:
									statusText = 'Unknown Status';
									statusClass = styles['status-unknown'];
									StatusIcon = null;
							}

							return (
								<Tag
									bordered={false}
									className={`${styles['statusTag']} ${statusClass}`}
								>
									<div className={styles['statusIcon']}>
										{StatusIcon}
										<span>{statusText}</span>
									</div>
								</Tag>
							);
						}}
					/>
					<Column
						title="Bank File"
						dataIndex="bankFile"
						key="bankFile"
						width={100}
						align="center"
						sorter={false}
						className="bg-white"
						render={(_, record: any) => (
							<Tooltip
								title={
									record.status === 'APPROVED'
										? 'Click to download bank files.'
										: 'Bank files will be available after payroll approval.'
								}
							>
								{record.status === 'APPROVED' ? (
									bankFileLoading[record.id] ? (
										// Show a loader while loading
										<Spin size="small" />
									) : (
										// Icon-only button to open modal
										<FileDoneOutlined
											style={{
												fontSize: '19px',
												cursor: 'pointer',
											}}
											onClick={() => {
												handleOpenBankFilesModal(
													record.id
												);
											}}
										/>
									)
								) : (
									<div className="text-gray-400">N/A</div>
								)}
							</Tooltip>
						)}
					/>
					<Column
						title="Action"
						key="action"
						width={140}
						align="center"
						className="bg-white"
						render={(_: any, record: any) => (
							<Space size={20}>
								{record.status !== 'APPROVED' &&
								isEdit &&
								record.status !== 'REJECTED' ? (
									<p
										className="cursor-pointer flex align-center justify-center"
										style={{ fontSize: 18 }}
										onClick={() =>
											navigate(
												`/run-payroll?payrollId=${record?.id}`
											)
										}
									>
										<EditOutlined />
									</p>
								) : (
									<p className="cursor-not-allowed flex align-center justify-center">
										<EditActionSvg color="#928F8F" />
									</p>
								)}

								{record.status !== 'APPROVED' &&
								isDelete &&
								record.status !== 'REJECTED' ? (
									<p
										className="cursor-pointer flex align-center justify-center"
										style={{
											fontSize: 18,
											cursor: 'pointer',
										}}
										onClick={() => {
											deleteDataHandler(record.id); // Log before calling handler
										}}
									>
										<DeleteActionSvg />
									</p>
								) : (
									<p className="cursor-not-allowed flex align-center justify-center">
										<DeleteActionSvg color="#928F8F" />
									</p>
								)}
							</Space>
						)}
					/>
					<Column
						title="Initiate Payment"
						dataIndex="initiatePayment"
						key="initiatePayment"
						width={170}
						align="center"
						sorter={false}
						className="bg-white"
						render={(_, record: any) => {
							const isDisabled =
								record.status !== 'APPROVED' ||
								!isAddInitiatePayment;
							return (
								<Tooltip title="Payment initiation is available once the payroll is approved.">
									{!isDisabled ? (
										<div
											onClick={() => {
												console.log(
													'record.currency: ',
													record.currency
												);
												setSelectedPayrollCurrency(
													record.currency
												);
												handleModalOpen(record.id);
											}}
											style={{
												cursor: 'pointer',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '19px',
												width: '100%',
												height: '100%',
											}}
										>
											<DollarOutlined
											// style={{
											// 	color: '#584495',
											// }}
											/>
										</div>
									) : (
										<div className="text-gray-400">N/A</div>
									)}
								</Tooltip>
							);
						}}
					/>
					<Column
						title="Check Status"
						dataIndex="initiatePayment"
						key="checkStatus"
						width={200}
						align="center"
						sorter={false}
						className="bg-white"
						render={(_, record: any) => {
							const isDisabled =
								{ record }.record?.status !== 'APPROVED';

							return (
								<Tooltip title="Status can be checked after payroll is approved">
									{!isDisabled ? (
										<Buttons
											buttons={[
												{
													text: 'Check Status',
													isLoading:
														checkStatusLoading[
															record.id
														] || false,
													className: 'btn-blue',
													isSubmit: false,
													onclick: () => {
														handleCheckStatus(
															record.id
														);
													},
													fontSize: '1.5rem',
													minWidth: '7rem',
													minHeight: '2rem',
												},
											]}
										/>
									) : (
										<div className="text-gray-400">N/A</div>
									)}
								</Tooltip>
							);
						}}
					/>
				</Table>
				{selectedPayrollId && selectedPayrollCurrency && (
					<AddModal
						isOpen={isModalVisible}
						handleCancel={handleCancel}
						bankDetailsData={bankDetailsData}
						isBankLoading={isBankLoading}
						payRollId={selectedPayrollId}
						handleBankDetails={handleBankDetails}
						currency={selectedPayrollCurrency}
					/>
				)}
			</div>
			{/* Bank Files Modal */}
			{isBankFilesModalVisible && (
				<Modal
					open={isBankFilesModalVisible}
					onCancel={handleCloseBankFilesModal}
					footer={null}
					closeIcon={null}
					width={600}
					styles={{
						body: { padding: 0 },
					}}
				>
					{/* Custom Header */}
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '5px 0px 8px 10px',
						}}
					>
						<Text strong style={{ fontSize: '20px' }}>
							Multi-Bank Payroll Files
						</Text>
						<Button
							type="text"
							icon={<CloseOutlined />}
							onClick={handleCloseBankFilesModal}
							style={{ marginRight: '-8px' }}
						/>
					</div>

					{/* Modal Content */}
					<div style={{ padding: '16px 12px' }}>
						{/* Note */}
						<div
							style={{
								backgroundColor: '#f4f1ff',
								paddingInline: '14px',
								paddingBlock: '8px',
								borderRadius: '6px',
								marginBottom: '12px',
								border: '1px solid #584495',
							}}
						>
							<Text
								style={{ fontSize: '14px', color: '#584495' }}
							>
								Download these files for manual payment via the
								bank portals
							</Text>
						</div>

						{/* Bank List */}
						<Space
							direction="vertical"
							style={{ width: '100%' }}
							size="middle"
						>
							{[
								{
									id: 'stanbic',
									name: 'Stanbic Bank',
									type: 'STANBIC_BANK',
									logo: 'assets/images/Stanbic-Bank-Logo.jpg',
								},
								{
									id: 'access',
									name: 'Access Bank',
									type: 'ACCESS_BANK',
									logo: 'assets/images/access-bank.png',
								},
								{
									id: 'indo-zam',
									name: 'Indo-Zam Bank',
									type: 'INDO_ZAMBIA_BANK',
									logo: 'assets/images/indo-zam-bank.jpg',
								},
								{
									id: 'zanaco',
									name: 'Zanaco Bank',
									type: 'ZANACO',
									logo: 'assets/images/zanaco-bank.jpg',
								},
							].map((bank, index) => {
										const csvLoadingKey = `${selectedPayrollForBankFiles}_${bank.type}_csv`;
										const excelLoadingKey = `${selectedPayrollForBankFiles}_${bank.type}_excel`;

										const isCsvLoading =
											bankFileLoading[csvLoadingKey];
										const isExcelLoading =
											bankFileLoading[excelLoadingKey];

								return (
									<div key={bank.id}>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												padding: '12px 0',
											}}
										>
											{/* Bank Info with Logo */}
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '12px',
												}}
											>
												<img
													src={bank.logo}
													alt={`${bank.name} logo`}
													style={{
														width: '70px',
														height: '50px',
														objectFit: 'contain',
														borderRadius: '4px',
													}}
												/>
												<Text
													style={{
														fontSize: '15px',
														fontWeight: 500,
													}}
												>
													{bank.name}
												</Text>
											</div>

											<Space size="small">
												<Tooltip title="Download CSV">
													<Button
														type="default"
														icon={
															isCsvLoading ? (
																<Spin size="small" />
															) : (
																<FileTextOutlined />
															)
														}
														onClick={() =>
															handleBankFileDownload(
																selectedPayrollForBankFiles!,
																bank.type,
																'csv'
															)
														}
														loading={isCsvLoading}
														disabled={
															isExcelLoading
														}
														style={{
															minWidth: '90px',
														}}
													>
														CSV
													</Button>
												</Tooltip>

												<Tooltip title="Download Excel">
													<Button
														type="primary"
														icon={
															isExcelLoading ? (
																<Spin size="small" />
															) : (
																<FileExcelOutlined />
															)
														}
														onClick={() =>
															handleBankFileDownload(
																selectedPayrollForBankFiles!,
																bank.type,
																'excel'
															)
														}
														loading={isExcelLoading}
														disabled={isCsvLoading}
														style={{
															minWidth: '90px',
														}}
													>
														Excel
													</Button>
												</Tooltip>
											</Space>
										</div>
										{index < 2 && (
											<Divider style={{ margin: 0 }} />
										)}
									</div>
								);
							})}
						</Space>
					</div>
				</Modal>
			)}

			<Modal
				open={isBankErrorModalVisible}
				onCancel={() => setIsBankErrorModalVisible(false)}
				footer={null}
				centered
				width={520}
				closable={false}
			>
				<div style={{ textAlign: 'center', padding: '10px 5px' }}>
					<div
						style={{
							width: 70,
							height: 70,
							borderRadius: '50%',
							background: '#FFF7E6',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							margin: '0 auto',
						}}
					>
						<ExclamationCircleOutlined
							style={{ fontSize: 38, color: '#faad14' }}
						/>
					</div>

					<h2 style={{ marginTop: 16, fontWeight: 700 }}>
						Bank Details Required
					</h2>

					<p
						style={{
							fontSize: 15,
							color: '#595959',
							marginTop: 8,
							lineHeight: 1.6,
						}}
					>
						To process payments, please add and verify a bank
						account in the Company Setup section.
					</p>

					<div
						style={{
							textAlign: 'left',
							background: '#fafafa',
							padding: '16px 20px',
							borderRadius: 8,
							marginTop: 24,
							border: '1px solid #f0f0f0',
						}}
					>
						<p
							style={{
								fontWeight: 600,
								marginBottom: 10,
								color: '#444',
							}}
						>
							Bank must meet the following requirements:
						</p>
						<ul
							style={{
								paddingLeft: 20,
								lineHeight: 1.6,
								margin: 0,
							}}
						>
							<li>
								Currency must match selected payroll currency
							</li>
							<li>
								Supported Bank Types:
								<div style={{ marginTop: 8, paddingLeft: 10 }}>
									<div style={{ marginBottom: 6 }}>
										<b>Stanbic Bank</b> — No Fund Transfer
										requirement
									</div>

									<div>
										<b>First Capital Bank</b>
										<ul
											style={{
												paddingLeft: 18,
												marginTop: 6,
											}}
										>
											<li>
												Fund Transfer must be enabled
											</li>
											<li>
												Account status: <b>Verified</b>
											</li>
										</ul>
									</div>
								</div>
							</li>
						</ul>
					</div>

					<Button
						type="primary"
						size="large"
						style={{ marginTop: 26, borderRadius: 6 }}
						onClick={handleRedirect}
					>
						Go to Company Setup
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default PayrollTable;
